import React, { useEffect, useMemo, useState } from "react";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import PageHeader from "../../components/ui/PageHeader";
import Switch from "../../components/ui/Switch";
import {
    ApiDistributor,
    createApiDistributorRequest,
    deleteApiDistributorRequest,
    listApiDistributorsRequest,
    rotateApiDistributorKeyRequest,
    setAllowedToursRequest,
    updateApiDistributorRequest,
} from "../../services/api/apiDistributors.api";
import { getToursRequest } from "../../services/api/event-content.api";
import { TourItem, ToursListResponse } from "../../services/types/event-content.types";

interface FormState {
    name: string;
    email: string;
    contactPhone: string;
    note: string;
    isActive: boolean;
}

const initialFormState: FormState = {
    name: "",
    email: "",
    contactPhone: "",
    note: "",
    isActive: true,
};

const formatDateTime = (value: string | null): string => {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("ru-RU");
    } catch {
        return value;
    }
};

const maskKey = (key: string): string => {
    if (!key) return "";
    if (key.length <= 12) return key;
    return `${key.slice(0, 6)}…${key.slice(-4)}`;
};

const extractTours = (response: ToursListResponse | TourItem[]): TourItem[] => {
    if (Array.isArray(response)) return response;
    return (
        response.items ??
        response.data ??
        response.results ??
        []
    );
};

const ApiDistributorsPage: React.FC = () => {
    const [items, setItems] = useState<ApiDistributor[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [formState, setFormState] = useState<FormState>(initialFormState);
    const [searchValue, setSearchValue] = useState("");
    const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const [revealedKeys, setRevealedKeys] = useState<Record<string, boolean>>({});
    const [justCreatedKey, setJustCreatedKey] = useState<string | null>(null);

    // ---- tours access ----
    const [allTours, setAllTours] = useState<TourItem[]>([]);
    const [isToursLoading, setIsToursLoading] = useState(false);
    const [toursError, setToursError] = useState<string | null>(null);
    const [allowedTourIds, setAllowedTourIds] = useState<Set<string>>(new Set());
    const [initialAllowedTourIds, setInitialAllowedTourIds] = useState<Set<string>>(new Set());
    const [toursSearch, setToursSearch] = useState("");
    const [isSavingTours, setIsSavingTours] = useState(false);
    const [toursSuccess, setToursSuccess] = useState<string | null>(null);

    const filteredItems = useMemo(() => {
        const query = searchValue.trim().toLowerCase();

        return items
            .filter((item) => {
                if (statusFilter === "active" && !item.is_active) return false;
                if (statusFilter === "inactive" && item.is_active) return false;
                if (!query) return true;
                return (
                    item.name.toLowerCase().includes(query) ||
                    (item.email ?? "").toLowerCase().includes(query) ||
                    (item.contact_phone ?? "").toLowerCase().includes(query)
                );
            })
            .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    }, [items, searchValue, statusFilter]);

    const filteredTours = useMemo(() => {
        const q = toursSearch.trim().toLowerCase();
        if (!q) return allTours;
        return allTours.filter(
            (t) =>
                t.name_ru?.toLowerCase().includes(q) ||
                t.name_en?.toLowerCase().includes(q) ||
                t.name_de?.toLowerCase().includes(q) ||
                t.name_ua?.toLowerCase().includes(q) ||
                (t.slug ?? "").toLowerCase().includes(q),
        );
    }, [allTours, toursSearch]);

    const hasToursChanges = useMemo(() => {
        if (allowedTourIds.size !== initialAllowedTourIds.size) return true;
        for (const id of allowedTourIds) {
            if (!initialAllowedTourIds.has(id)) return true;
        }
        return false;
    }, [allowedTourIds, initialAllowedTourIds]);

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const response = await listApiDistributorsRequest();
            setItems(response);
        } catch (error) {
            setPageError(
                error instanceof Error ? error.message : "Не удалось загрузить распространителей",
            );
        } finally {
            setIsLoading(false);
        }
    };

    const loadTours = async () => {
        setIsToursLoading(true);
        setToursError(null);
        try {
            // Тянем постранично (лимит сервера — 100).
            const pageSize = 100;
            const collected: TourItem[] = [];
            const seen = new Set<string>();
            let page = 1;
            const maxPages = 50; // страховка от бесконечного цикла

            while (page <= maxPages) {
                const response = await getToursRequest({ page, size: pageSize });
                const chunk = extractTours(response);
                if (chunk.length === 0) break;

                let added = 0;
                for (const t of chunk) {
                    if (!seen.has(t.id)) {
                        seen.add(t.id);
                        collected.push(t);
                        added += 1;
                    }
                }

                // Меньше чем полный размер страницы — значит это последняя.
                if (chunk.length < pageSize) break;
                // Ничего нового не пришло — тоже стопаем (защита от циклов).
                if (added === 0) break;

                page += 1;
            }

            setAllTours(collected);
        } catch (error) {
            setToursError(
                error instanceof Error ? error.message : "Не удалось загрузить туры",
            );
        } finally {
            setIsToursLoading(false);
        }
    };

    useEffect(() => {
        void loadData();
        void loadTours();
    }, []);

    const applyAllowedFromItem = (item: ApiDistributor) => {
        const ids = new Set<string>((item.allowed_tours ?? []).map((t) => t.id));
        setAllowedTourIds(ids);
        setInitialAllowedTourIds(new Set(ids));
        setToursSuccess(null);
    };

    const resetForm = () => {
        setSelectedId(null);
        setFormState(initialFormState);
        setFormError(null);
        setSuccessMessage(null);
        setJustCreatedKey(null);
        setAllowedTourIds(new Set());
        setInitialAllowedTourIds(new Set());
        setToursSuccess(null);
        setToursSearch("");
    };

    const handleSelect = (item: ApiDistributor) => {
        setSelectedId(item.id);
        setFormState({
            name: item.name,
            email: item.email ?? "",
            contactPhone: item.contact_phone ?? "",
            note: item.note ?? "",
            isActive: item.is_active,
        });
        setFormError(null);
        setSuccessMessage(null);
        setJustCreatedKey(null);
        applyAllowedFromItem(item);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);
        setSuccessMessage(null);

        if (!formState.name.trim()) {
            setFormError("Название обязательно.");
            return;
        }

        setIsSaving(true);

        try {
            if (selectedId) {
                const updated = await updateApiDistributorRequest(selectedId, {
                    name: formState.name.trim(),
                    email: formState.email.trim() || null,
                    contact_phone: formState.contactPhone.trim() || null,
                    note: formState.note.trim() || null,
                    is_active: formState.isActive,
                });
                setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
                handleSelect(updated);
                setSuccessMessage("Распространитель обновлён.");
            } else {
                const created = await createApiDistributorRequest({
                    name: formState.name.trim(),
                    email: formState.email.trim() || null,
                    contact_phone: formState.contactPhone.trim() || null,
                    note: formState.note.trim() || null,
                });
                setItems((prev) => [created, ...prev]);
                setSelectedId(created.id);
                setJustCreatedKey(created.api_key);
                setRevealedKeys((prev) => ({ ...prev, [created.id]: true }));
                setSuccessMessage("Распространитель создан. Сохраните API-ключ — он показан ниже.");
                setFormState({
                    name: created.name,
                    email: created.email ?? "",
                    contactPhone: created.contact_phone ?? "",
                    note: created.note ?? "",
                    isActive: created.is_active,
                });
                applyAllowedFromItem(created);
            }
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Ошибка сохранения");
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggleActive = async (item: ApiDistributor, event: React.MouseEvent) => {
        event.stopPropagation();
        try {
            const updated = await updateApiDistributorRequest(item.id, {
                is_active: !item.is_active,
            });
            setItems((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
            if (selectedId === updated.id) {
                setFormState((prev) => ({ ...prev, isActive: updated.is_active }));
            }
        } catch (error) {
            setPageError(error instanceof Error ? error.message : "Не удалось изменить статус");
        }
    };

    const handleRotateKey = async () => {
        if (!selectedId) return;
        // eslint-disable-next-line no-alert
        if (!window.confirm("Выпустить новый API-ключ? Старый перестанет работать сразу.")) {
            return;
        }
        try {
            const { api_key } = await rotateApiDistributorKeyRequest(selectedId);
            setItems((prev) =>
                prev.map((it) => (it.id === selectedId ? { ...it, api_key } : it)),
            );
            setJustCreatedKey(api_key);
            setRevealedKeys((prev) => ({ ...prev, [selectedId]: true }));
            setSuccessMessage("Новый API-ключ выпущен. Скопируйте — он показан ниже.");
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось выпустить новый ключ");
        }
    };

    const handleDelete = async () => {
        if (!selectedId) return;
        // eslint-disable-next-line no-alert
        if (!window.confirm("Удалить распространителя безвозвратно?")) return;
        try {
            await deleteApiDistributorRequest(selectedId);
            setItems((prev) => prev.filter((it) => it.id !== selectedId));
            resetForm();
            setSuccessMessage("Распространитель удалён.");
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось удалить");
        }
    };

    const handleCopyKey = async (key: string) => {
        try {
            await navigator.clipboard.writeText(key);
            setSuccessMessage("Ключ скопирован в буфер обмена.");
        } catch {
            setFormError("Не удалось скопировать ключ.");
        }
    };

    const toggleKeyReveal = (id: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setRevealedKeys((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    // ---- tours access handlers ----

    const handleToggleTour = (tourId: string) => {
        setAllowedTourIds((prev) => {
            const next = new Set(prev);
            if (next.has(tourId)) {
                next.delete(tourId);
            } else {
                next.add(tourId);
            }
            return next;
        });
        setToursSuccess(null);
    };

    const handleSelectAllFiltered = () => {
        setAllowedTourIds((prev) => {
            const next = new Set(prev);
            for (const t of filteredTours) next.add(t.id);
            return next;
        });
        setToursSuccess(null);
    };

    const handleClearAllFiltered = () => {
        setAllowedTourIds((prev) => {
            const next = new Set(prev);
            for (const t of filteredTours) next.delete(t.id);
            return next;
        });
        setToursSuccess(null);
    };

    const handleResetTours = () => {
        setAllowedTourIds(new Set(initialAllowedTourIds));
        setToursSuccess(null);
    };

    const handleSaveTours = async () => {
        if (!selectedId) return;
        setIsSavingTours(true);
        setToursError(null);
        setToursSuccess(null);
        try {
            const updated = await setAllowedToursRequest(
                selectedId,
                Array.from(allowedTourIds),
            );
            const shortList = updated.allowed_tours ?? [];
            const nextIds = new Set<string>(shortList.map((t) => t.id));
            setAllowedTourIds(nextIds);
            setInitialAllowedTourIds(new Set(nextIds));

            // Обновляем распространителя в списке целиком.
            setItems((prev) =>
                prev.map((it) => (it.id === updated.id ? updated : it)),
            );
            setToursSuccess("Доступ к турам обновлён.");
        } catch (error) {
            setToursError(
                error instanceof Error ? error.message : "Не удалось сохранить доступ к турам",
            );
        } finally {
            setIsSavingTours(false);
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="API-распространители"
                description="Внешние компании, продающие билеты через наш публичный API (/api/v1)."
                actions={
                    <Button onClick={resetForm}>Новый распространитель</Button>
                }
            />

            <section className="content-split">
                <Card className="content-list-card">
                    <div className="section-toolbar">
                        <div>
                            <h3 className="card-title">Список распространителей</h3>
                            <p className="card-text">
                                Поиск, включение/отключение и просмотр ключей.
                            </p>
                        </div>

                        <div className="section-toolbar-controls">
                            <Input
                                type="text"
                                placeholder="Поиск по названию / email / телефону..."
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                            />

                            <select
                                className="ui-input"
                                style={{ minWidth: 180 }}
                                value={statusFilter}
                                onChange={(event) =>
                                    setStatusFilter(
                                        event.target.value as "all" | "active" | "inactive",
                                    )
                                }
                            >
                                <option value="all">Все статусы</option>
                                <option value="active">Только активные</option>
                                <option value="inactive">Только отключённые</option>
                            </select>
                        </div>
                    </div>

                    {pageError ? (
                        <div className="form-global-error">{pageError}</div>
                    ) : isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Нет данных</h3>
                            <p className="empty-block-text">
                                Создайте первого API-распространителя.
                            </p>
                        </div>
                    ) : (
                        <div className="table-wrap">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Название</th>
                                        <th>Email</th>
                                        <th>Телефон</th>
                                        <th>API-ключ</th>
                                        <th>Туры</th>
                                        <th>Использован</th>
                                        <th>Статус</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredItems.map((item) => (
                                        <tr
                                            key={item.id}
                                            className={
                                                item.id === selectedId ? "data-table-row-active" : ""
                                            }
                                            onClick={() => handleSelect(item)}
                                        >
                                            <td>
                                                <div className="table-primary-cell">
                                                    <span className="table-primary-text">
                                                        {item.name}
                                                    </span>
                                                    <span className="table-secondary-text">
                                                        {item.id}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>{item.email ?? "—"}</td>
                                            <td>{item.contact_phone ?? "—"}</td>
                                            <td>
                                                <code style={{ fontSize: 12 }}>
                                                    {revealedKeys[item.id]
                                                        ? item.api_key
                                                        : maskKey(item.api_key)}
                                                </code>
                                                <button
                                                    type="button"
                                                    style={{
                                                        marginLeft: 8,
                                                        background: "none",
                                                        border: "none",
                                                        color: "var(--accent, #2b6cb0)",
                                                        cursor: "pointer",
                                                        padding: 0,
                                                        fontSize: 12,
                                                    }}
                                                    onClick={(e) => toggleKeyReveal(item.id, e)}
                                                >
                                                    {revealedKeys[item.id] ? "Скрыть" : "Показать"}
                                                </button>
                                            </td>
                                            <td>
                                                <span className="table-secondary-text">
                                                    {item.allowed_tours?.length ?? 0}
                                                </span>
                                            </td>
                                            <td>{formatDateTime(item.last_used_at)}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className={`status-badge ${
                                                        item.is_active
                                                            ? "status-badge-active"
                                                            : "status-badge-inactive"
                                                    }`}
                                                    onClick={(e) => handleToggleActive(item, e)}
                                                    title={
                                                        item.is_active
                                                            ? "Кликните, чтобы деактивировать"
                                                            : "Кликните, чтобы активировать"
                                                    }
                                                >
                                                    {item.is_active ? "Активен" : "Отключён"}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>

                <Card className="content-form-card">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">
                                {selectedId
                                    ? "Редактирование распространителя"
                                    : "Новый распространитель"}
                            </h3>
                            <p className="card-text">
                                Название, контакты, комментарий и статус. API-ключ выпускается
                                автоматически при создании.
                            </p>
                        </div>
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        <div className="form-grid">
                            <Field label="Название" htmlFor="api-dist-name">
                                <Input
                                    id="api-dist-name"
                                    value={formState.name}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            name: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Email" htmlFor="api-dist-email">
                                <Input
                                    id="api-dist-email"
                                    type="email"
                                    value={formState.email}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            email: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Телефон" htmlFor="api-dist-phone">
                                <Input
                                    id="api-dist-phone"
                                    value={formState.contactPhone}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            contactPhone: event.target.value,
                                        }))
                                    }
                                />
                            </Field>
                        </div>

                        <Field label="Комментарий" htmlFor="api-dist-note">
                            <textarea
                                id="api-dist-note"
                                className="ui-textarea"
                                rows={4}
                                value={formState.note}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        note: event.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <div className="form-switch-row">
                            <Switch
                                checked={formState.isActive}
                                onChange={(checked) =>
                                    setFormState((prev) => ({ ...prev, isActive: checked }))
                                }
                                label={
                                    formState.isActive
                                        ? "Распространитель активен"
                                        : "Распространитель отключён"
                                }
                            />
                        </div>

                        {selectedId ? (
                            <div
                                style={{
                                    padding: 12,
                                    background: "var(--surface-muted)",
                                    border: "1px solid var(--border)",
                                    borderRadius: 10,
                                    marginTop: 8,
                                    color: "var(--text)",
                                }}
                            >
                                <div>
                                    <span className="table-secondary-text">API-ключ:</span>{" "}
                                    <code style={{ fontSize: 13 }}>
                                        {justCreatedKey ??
                                            (revealedKeys[selectedId]
                                                ? items.find((it) => it.id === selectedId)?.api_key
                                                : maskKey(
                                                      items.find((it) => it.id === selectedId)
                                                          ?.api_key ?? "",
                                                  ))}
                                    </code>
                                </div>
                                <div className="editor-actions" style={{ marginTop: 8 }}>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={(e: React.MouseEvent) =>
                                            toggleKeyReveal(selectedId, e)
                                        }
                                    >
                                        {revealedKeys[selectedId] ? "Скрыть ключ" : "Показать ключ"}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() => {
                                            const item = items.find((it) => it.id === selectedId);
                                            if (item) void handleCopyKey(item.api_key);
                                        }}
                                    >
                                        Скопировать
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={handleRotateKey}
                                    >
                                        Выпустить новый ключ
                                    </Button>
                                </div>
                            </div>
                        ) : null}

                        {formError ? <div className="form-global-error">{formError}</div> : null}
                        {successMessage ? (
                            <div className="form-global-success">{successMessage}</div>
                        ) : null}

                        <div className="editor-actions">
                            <Button type="submit" disabled={isSaving}>
                                {isSaving
                                    ? "Сохраняем..."
                                    : selectedId
                                    ? "Сохранить изменения"
                                    : "Создать"}
                            </Button>

                            <Button type="button" variant="secondary" onClick={resetForm}>
                                Очистить форму
                            </Button>

                            {selectedId ? (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={handleDelete}
                                >
                                    Удалить
                                </Button>
                            ) : null}
                        </div>
                    </form>
                </Card>
            </section>

            {selectedId ? (
                <section style={{ marginTop: 16 }}>
                    <Card className="content-form-card">
                        <div className="editor-head">
                            <div>
                                <h3 className="card-title">Доступ к турам</h3>
                                <p className="card-text">
                                    Отметьте туры, которые распространитель видит через API. Если
                                    ни один тур не отмечен — распространитель не увидит и не сможет
                                    продавать ничего.
                                </p>
                            </div>
                        </div>

                        <div className="section-toolbar" style={{ marginBottom: 12 }}>
                            <div className="section-toolbar-controls" style={{ flexWrap: "wrap", gap: 8 }}>
                                <Input
                                    type="text"
                                    placeholder="Поиск тура..."
                                    value={toursSearch}
                                    onChange={(event) => setToursSearch(event.target.value)}
                                />
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={handleSelectAllFiltered}
                                >
                                    Выбрать все (в списке)
                                </Button>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={handleClearAllFiltered}
                                >
                                    Снять все (в списке)
                                </Button>
                            </div>
                            <div className="table-secondary-text">
                                Выбрано: {allowedTourIds.size} из {allTours.length}
                            </div>
                        </div>

                        {toursError ? (
                            <div className="form-global-error">{toursError}</div>
                        ) : isToursLoading ? (
                            <div className="table-skeleton">
                                <div className="table-skeleton-row" />
                                <div className="table-skeleton-row" />
                                <div className="table-skeleton-row" />
                            </div>
                        ) : filteredTours.length === 0 ? (
                            <div className="empty-block">
                                <h3 className="empty-block-title">Туры не найдены</h3>
                                <p className="empty-block-text">
                                    Измените поисковый запрос или создайте туры в разделе «Туры».
                                </p>
                            </div>
                        ) : (
                            <div
                                style={{
                                    maxHeight: 420,
                                    overflowY: "auto",
                                    background: "var(--surface-muted)",
                                    border: "1px solid var(--border)",
                                    borderRadius: 10,
                                    padding: 8,
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                                    gap: 6,
                                }}
                            >
                                {filteredTours.map((tour) => {
                                    const checked = allowedTourIds.has(tour.id);
                                    return (
                                        <label
                                            key={tour.id}
                                            style={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: 8,
                                                padding: "8px 10px",
                                                borderRadius: 8,
                                                cursor: "pointer",
                                                color: "var(--text)",
                                                background: checked
                                                    ? "var(--primary-soft)"
                                                    : "var(--surface-elevated)",
                                                border: `1px solid ${
                                                    checked
                                                        ? "var(--primary)"
                                                        : "var(--border)"
                                                }`,
                                                transition: "background var(--transition), border-color var(--transition)",
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => handleToggleTour(tour.id)}
                                                style={{ marginTop: 3, accentColor: "var(--primary)" }}
                                            />
                                            <div style={{ display: "flex", flexDirection: "column" }}>
                                                <span style={{ fontWeight: 500, color: "var(--text)" }}>
                                                    {tour.name_ru || tour.name_de || tour.name_en}
                                                </span>
                                                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                    {tour.slug ?? tour.id}
                                                </span>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        )}

                        {toursSuccess ? (
                            <div className="form-global-success" style={{ marginTop: 12 }}>
                                {toursSuccess}
                            </div>
                        ) : null}

                        <div className="editor-actions" style={{ marginTop: 12 }}>
                            <Button
                                type="button"
                                onClick={handleSaveTours}
                                disabled={isSavingTours || !hasToursChanges}
                            >
                                {isSavingTours ? "Сохраняем..." : "Сохранить доступ к турам"}
                            </Button>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleResetTours}
                                disabled={isSavingTours || !hasToursChanges}
                            >
                                Отменить изменения
                            </Button>
                        </div>
                    </Card>
                </section>
            ) : null}
        </div>
    );
};

export default ApiDistributorsPage;
